import {NavLink, ufn} from '@edifecs/fp-ui-lib';
import {AppConstants} from '@app/AppConstants';
import {Injectable, Injector, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from './AppConfigService';
import {EdfxNavUi, NavLinkInterceptor, NavLinksProvider, ShellService} from '@edifecs/fp-ui-shell';
import {AppInfoComponent} from '@app/core/components/AppInfoComponent';
import {SubtenantListComponent} from '@app/core/components/SubtenantListComponent';
import {EnvService} from '@app/core/services/EnvService';
import {EnvNavLinkComponent} from '@app/core/components/EnvNavLinkComponent';
import {EnvListComponent} from '@app/core/components/EnvListComponent';
import {Subscription} from 'rxjs';
import {EdfSettingsService} from '@edfx/edifecs-ui-lib';
import {StringUtils} from '@app/core/util/StringUtils';


/**
 * Provides nav links for side nav bar.
 * Also handles nav kink actions.
 */
@Injectable({providedIn: 'root'})
export class NavLinksProviderService implements NavLinksProvider, NavLinkInterceptor, OnDestroy {
    /**
     * App logo nav link
     * @private
     */
    private static readonly NAV_LINK_APP_LOGO: NavLink = {
        // initialized in getNavLinks()
        component: {component: AppInfoComponent},
        noSelect: true
    };

    /**
     * Environment switcher nav link
     * @private
     */
    private static readonly NAV_LINK_ENV_SWITCHER: NavLink = {
        title: 'Environments',
        component: {component: EnvNavLinkComponent},
        noSelect: true
    };

    /**
     * Subtenants nav link
     * @private
     */
    private static readonly NAV_LINK_SUBTENANTS: NavLink = {
        title: 'Subtenants',
        iconUrl: 'assets/images/navbar/ic_subtenants.svg',
        noSelect: true
    };

    /**
     * User profile nav link
     * @private
     */
    private static readonly NAV_LINK_PROFILE: NavLink = {
        title: 'Profile',
        iconUrl: 'assets/images/navbar/ic_profile.svg',
        noSelect: true
    };

    /**
     * Frontend settings
     */
    showFpEnvInfo?: boolean;

    /**
     * Frontend settings subscription
     */
    edfSettingsSubscription?: Subscription;

    /**
     * Constructor
     * @param httpClient HTTP client
     * @param appConfig App configuration
     * @param envSvc Environment service
     * @param shellSvc Shell service
     * @param injector App injector
     * @param edfSettings Edifecs Settings service
     */
    constructor(private readonly httpClient: HttpClient,
                private readonly appConfig: AppConfigService,
                private readonly envSvc: EnvService,
                private readonly shellSvc: ShellService,
                private readonly injector: Injector,
                private edfSettings: EdfSettingsService) {
        this.edfSettingsSubscription = this.edfSettings.settings.subscribe((settings: any) => {
            this.showFpEnvInfo = settings?.showFpEnvInfo;
        });
    }

    /**
     * Parse a nav link
     * @param data nav link data
     * @private
     */
    private parseNavLink(data: any): NavLink {
        const link: NavLink = {};
        link.title = data?.title;
        link.path = data?.path;
        link.iconUrl = data?.iconUrl;
        link.isDefault = data?.default ?? false;
        link.isHome = data?.home ?? false;

        const subItems = data?.subItems;
        if (ufn.isArray(subItems)) {
            link.subItems = subItems.filter(n => n != null).map(n => this.parseNavLink(n));
        } else {
            link.subItems = [];
        }
        return link;
    }

    /**
     * non app nav links
     */
    async initNavLinks(): Promise<NavLink[]> {
        const links: NavLink[] = [];
        // add app logo nav link
        NavLinksProviderService.NAV_LINK_APP_LOGO.component = {
            component: AppInfoComponent,
            props: {
                logo: this.appConfig.navLogoImage,
                title: this.appConfig.navLogoTitle
            }
        };

        // Not to show app logo/title when access from ump
        if (!AppConstants.UMP_SVC_ENABLED) {
            links.push({title: this.appConfig.navLogoTitle, iconUrl: this.appConfig.navLogoImage,
                component: NavLinksProviderService.NAV_LINK_APP_LOGO.component});
        }
        return links;
    }

    /**
     * app specific nav links
     */
    async getNavLinks(): Promise<NavLink[]> {
        // get nav links from API
        const navData: any = await this.httpClient.get(AppConstants.NAV_CONFIG_PATH, {responseType: 'json'})
            .toPromise();

        let links: any[] = [];

        // add server switcher
        if (this.envSvc.isEnabled) {
            NavLinksProviderService.NAV_LINK_ENV_SWITCHER.title = this.getSwitcherTitle();
            links.push(NavLinksProviderService.NAV_LINK_ENV_SWITCHER);
        }

        // add nav links from data
        const parsed = ufn.asArray(navData?.navLinks).filter(n => n != null).map(n => this.parseNavLink(n));
        links.push(...parsed);

        // add subtenant nav link
        if (this.appConfig.currentServerSubtenantsEnabled) {
            links.push(NavLinksProviderService.NAV_LINK_SUBTENANTS);
            NavLinksProviderService.NAV_LINK_SUBTENANTS.spacerBefore = true;
        }

        // add profile nav link
        // if (!AppConstants.CMFA_MODE) {
        //     links.push(NavLinksProviderService.NAV_LINK_PROFILE);
        //     NavLinksProviderService.NAV_LINK_PROFILE.spacerBefore = !links.some(
        //         n => n.spacerBefore && n !== NavLinksProviderService.NAV_LINK_PROFILE);
        // }

        links = links.map((link) => {
            if (link.subItems && link.subItems.length > 0) {
                link.path = link.subItems[0].path
            }
            return link;
        });
        return links;
    }

    /**
     * @internal
     * Get title text to display.
     */
    getSwitcherTitle(): string {
        // 4 characters from env short id
        const esn = StringUtils.parseText(this.envSvc.current?.env?.shortName, 4);
        // 2 characters from server short id
        const ssn = StringUtils.parseText(this.envSvc?.current?.srv?.shortName, 2);

        const sn = this.envSvc?.current?.srv?.name;
        const title = !this.showFpEnvInfo ? [ssn].filter(ufn.isText).join(':') : [esn, ssn].filter(ufn.isText).join(':');
        // joined short name and name with ::: which is used in collapse and expanded sidebar view
        return sn ? [title, sn].join(':::') : title;
    }

    /**
     * @internal
     * @inheritDoc
     */
    onNavLinkClicked(evt: { navLink: NavLink; openInNewTab: boolean }, next: () => void): void {
        switch (evt.navLink) {
            case NavLinksProviderService.NAV_LINK_APP_LOGO:
                // show about app UI
                if (AppConstants.CMFA_MODE) {
                    AppInfoComponent.show(this.shellSvc, this.injector);
                } else {
                    EdfxNavUi.showAboutAppUI(this.shellSvc);
                }
                break;
            case NavLinksProviderService.NAV_LINK_ENV_SWITCHER:
                // open environment switcher
                if (!AppConstants.CMFA_MODE) {
                    EnvListComponent.show(this.shellSvc);
                }
                break;
            case NavLinksProviderService.NAV_LINK_SUBTENANTS:
                // open subtenant search
                if (!AppConstants.CMFA_MODE) {
                    SubtenantListComponent.show(this.shellSvc);
                }
                break;
            case NavLinksProviderService.NAV_LINK_PROFILE:
                // show profile
                if (!AppConstants.CMFA_MODE) {
                    EdfxNavUi.showProfileUI(this.shellSvc);
                }
                break;
            default:
                // allow shell to navigate per the nav link path
                next();
                break;
        }
    }

    ngOnDestroy(): void {
        if (this.edfSettingsSubscription) {
            this.edfSettingsSubscription.unsubscribe();
        }
    }
}
